import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useSalesOrderDetailService from '../../../app/services/hooks/useSalesOrderDetailService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { SalesOrderDetailDataForm, defaultSalesOrderDetailDataForm } from '../../../app/models/SalesOrderDetail';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import useProductService from '../../../app/services/hooks/useProductService';
import useUnitService from '../../../app/services/hooks/useUnitService';
import useCurrencyService from '../../../app/services/hooks/useCurrencyService';
import { Product } from '../../../app/models/Product';
import { Unit } from '../../../app/models/Unit';
import { Currency } from '../../../app/models/Currency';
import SalesOrderDetailForm from './forms/SalesOrderDetailForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';

interface Props {
    salesOrderId: number;
    orderCurrencyId?: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const SalesOrderDetailCreate = ({ salesOrderId, orderCurrencyId, onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    
    const { storeSalesOrderDetail, fetchingStoreSalesOrderDetail } = useSalesOrderDetailService();
    const { getProducts } = useProductService();
    const { getUnits } = useUnitService();
    const { getCurrencies } = useCurrencyService();

    const [detail, setDetail] = useState<SalesOrderDetailDataForm>({
        ...defaultSalesOrderDetailDataForm,
        sales_order_id: salesOrderId,
        price_currency_id: orderCurrencyId
    });
    const [errorFields, setErrorFields] = useState<any>();
    const [products, setProducts] = useState<Product[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        loadInitialData();
    }, []);

    // Si cambia la moneda de la orden y el detalle no tiene moneda aún, heredamos
    useEffect(() => {
        if (orderCurrencyId && !detail.price_currency_id) {
            setDetail(prev => ({
                ...prev,
                price_currency_id: orderCurrencyId
            }));
        }
    }, [orderCurrencyId]);

    const loadInitialData = () => {
        if (showLoading) showLoading('loading', 'Cargando datos...');

        // Cargar productos
        getProducts(
            { page: 1, per_page: 1000, sort: 'id', order: 'asc' },
            {
                onSuccess: (response: ServiceResponse) => {
                    setProducts(response.data.products || []);
                }
            }
        );

        // Cargar unidades
        getUnits(
            { page: 1, per_page: 1000, sort: 'id', order: 'asc' },
            {
                onSuccess: (response: ServiceResponse) => {
                    setUnits(response.data.units || []);
                }
            }
        );

        // Cargar monedas
        getCurrencies(
            { page: 1, per_page: 1000, sort: 'id', order: 'asc' },
            {
                onSuccess: (response: ServiceResponse) => {
                    setCurrencies(response.data.currencies || []);
                    setLoadingData(false);
                    if (hideLoading) hideLoading();
                }
            }
        );
    };

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando posición...');

        storeSalesOrderDetail(detail, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                
                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                toast.success(response.message, { autoClose: 2500 });
                
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    if (loadingData) {
        return <div>Cargando datos...</div>;
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <SalesOrderDetailForm
                        salesOrderDetail={detail}
                        setSalesOrderDetail={setDetail}
                        products={products}
                        units={units}
                        currencies={currencies}
                        errorFields={errorFields}
                    />
                </div>
            </div>
            
            <hr />
            
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm 
                        callbackCancel={cancel} 
                        locked={fetchingStoreSalesOrderDetail} 
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm 
                        callbackSave={store} 
                        locked={fetchingStoreSalesOrderDetail} 
                    />
                </div>
            </div>
        </>
    );
};

export default SalesOrderDetailCreate;

